import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Repeat, CalendarClock, AlertTriangle, Plus, CheckCircle2, Ban, FileText } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { AddSubscriptionDialog } from "@/components/subscriptions/AddSubscriptionDialog";
import { UpcomingRenewalsCard } from "@/components/subscriptions/UpcomingRenewalsCard";

const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money = (v, cur) => `${cur} ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const INTERVALS = { weekly: "wöchentlich", monthly: "monatlich", quarterly: "quartalsweise", yearly: "jährlich" };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("de-CH") : "–");

function Kpi({ icon: Icon, label, value, sub, testid, tone = "" }) {
  return (
    <Card className={`p-4 border border-slate-200 bg-white ${tone}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-500"><Icon className="h-4 w-4" />{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function StatusBadge({ row }) {
  if (row.status === "ended") return <span className="rounded px-2 py-0.5 text-xs bg-slate-100 text-slate-600" data-testid={`sub-status-${row.id}`}>Beendet</span>;
  if (row.stale) return (
    <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs bg-rose-100 text-rose-800 font-medium" data-testid={`sub-status-${row.id}`} title={`Letzte Abbuchung vor ${row.days_since_last} Tagen – Schwelle ${row.stale_threshold_days} Tage`}>
      <AlertTriangle className="h-3 w-3" />Seit {Math.round(row.days_since_last / 30)} Mt. nicht abgebucht
    </span>
  );
  return <span className="rounded px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800" data-testid={`sub-status-${row.id}`}>Laufend</span>;
}

export default function SubscriptionsOverview() {
  const { company } = useCompany();
  const companyId = company?._id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    apiClient.get(`/companies/${companyId}/subscriptions-overview`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [companyId]);
  useEffect(load, [load]);

  const setStatus = async (row, status) => {
    try {
      await apiClient.post(`/companies/${companyId}/subscriptions-overview/${encodeURIComponent(row.id)}/status`, { status });
      toast.success(status === "ended" ? `«${row.title}» als beendet markiert.` : `«${row.title}» wieder als laufend markiert.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Status konnte nicht gespeichert werden."); }
  };

  const rows = (data?.subscriptions || []).filter((r) => showEnded || r.status !== "ended");
  const t = data?.totals;

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Abo-Übersicht</h1>
          <p className="text-sm text-slate-500">Alle laufenden Abos aus erkannten Beleg-Serien und Verträgen – Monats- und Jahreskosten in CHF. Rot: seit ≥2 Monaten über den Zyklus hinaus nicht mehr abgebucht, möglicherweise beendet.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={showEnded} onChange={(e) => setShowEnded(e.target.checked)} data-testid="subs-show-ended" />Beendete anzeigen</label>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="subs-add-btn"><Plus className="h-4 w-4 mr-1" />Abo erfassen</Button>
        </div>
      </div>

      {loading && <div className="text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={Repeat} label="Laufende Abos" value={t.active.count} sub={t.stale.count ? `+ ${t.stale.count} ohne aktuelle Abbuchung` : "alle aktuell abgebucht"} testid="subs-kpi-count" />
            <Kpi icon={CalendarClock} label="Kosten pro Monat" value={chf(t.active.monthly_chf)} sub="hochgerechnet, laufende Abos" testid="subs-kpi-monthly" />
            <Kpi icon={CalendarClock} label="Kosten pro Jahr" value={chf(t.active.yearly_chf)} sub="hochgerechnet, laufende Abos" testid="subs-kpi-yearly" />
            <Kpi icon={AlertTriangle} label="Seit ≥2 Monaten nicht abgebucht" value={t.stale.count} sub={t.stale.count ? `${chf(t.stale.monthly_chf)}/Mt. – prüfen, ob beendet` : "keine Auffälligkeiten"} testid="subs-kpi-stale" tone={t.stale.count ? "border-rose-200 bg-rose-50/40" : ""} />
          </div>

          <UpcomingRenewalsCard companyId={companyId} />

          <Card className="p-4 border border-slate-200 bg-white overflow-x-auto" data-testid="subs-table">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500"><tr><th className="text-left py-1">Abo</th><th className="text-left">Zyklus</th><th className="text-right">Betrag</th><th className="text-right">pro Monat</th><th className="text-right">pro Jahr</th><th className="text-right">Belege</th><th className="text-left pl-3">Letzte Abbuchung</th><th className="text-left">Status</th><th></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-t border-slate-100 ${r.stale ? "bg-rose-50/50" : ""} ${r.status === "ended" ? "text-slate-400" : ""}`} data-testid={`sub-row-${r.id}`}>
                    <td className="py-2">
                      <div className="font-medium text-slate-900">{r.title}</div>
                      <div className="text-xs text-slate-500">{r.partner}{r.account ? ` · Konto ${r.account}` : ""}{r.source === "contract" ? (r.manual ? " · manuell" : " · Vertrag") : ""}{r.open_count ? ` · ${r.open_count} offen im Posteingang` : ""}</div>
                    </td>
                    <td className="text-xs">{INTERVALS[r.interval] || r.interval}</td>
                    <td className="text-right tabular-nums">{money(r.amount, r.currency)}{r.currency !== "CHF" && <div className="text-[11px] text-slate-500">≈ {chf(r.amount_chf)}</div>}</td>
                    <td className="text-right tabular-nums font-medium">{chf(r.monthly_cost_chf)}</td>
                    <td className="text-right tabular-nums font-medium">{chf(r.yearly_cost_chf)}</td>
                    <td className="text-right tabular-nums">{r.count}</td>
                    <td className="pl-3 text-xs">{fmtDate(r.last_date)}{r.next_expected && r.status !== "ended" && <div className="text-[11px] text-slate-500">nächste ~ {fmtDate(r.next_expected)}</div>}</td>
                    <td><StatusBadge row={r} /></td>
                    <td className="text-right whitespace-nowrap">
                      {r.status === "ended"
                        ? <Button size="sm" variant="ghost" onClick={() => setStatus(r, "active")} data-testid={`sub-reactivate-${r.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Läuft wieder</Button>
                        : <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => setStatus(r, "ended")} data-testid={`sub-end-${r.id}`}><Ban className="h-4 w-4 mr-1" />Beendet</Button>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-500 text-xs" data-testid="subs-empty"><FileText className="h-5 w-5 mx-auto mb-1 text-slate-300" />Noch keine Abos erkannt. Abos werden ab 3 Belegen desselben Lieferanten mit gleichem Betrag erkannt – oder über «Abo erfassen» manuell anlegen.</td></tr>}
              </tbody>
            </table>
            {Object.keys(data.fx || {}).length > 0 && <div className="mt-2 text-[11px] text-slate-400" data-testid="subs-fx-note">Umrechnung zum Tageskurs: {Object.entries(data.fx).map(([k, v]) => `1 ${k} = ${Number(v).toFixed(4)} CHF`).join(" · ")}</div>}
          </Card>
        </>
      )}
      <AddSubscriptionDialog open={addOpen} onOpenChange={setAddOpen} companyId={companyId} onSaved={load} />
    </div>
  );
}
